import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, CardContent, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TakeReturnAction from "../../Modals/takereturnaction";

const initialTimeline = [
    {
        title: "Return Requested",
        date: "12/12/2025, 10:00:00 AM",
        icon: <RadioButtonUncheckedIcon />,
    },
    {
        title: "Vendor Approved Request",
        date: "12/12/2025, 2:30:00 PM",
        icon: <CheckCircleIcon />,
    },
    {
        title: "Admin Approved Pickup",
        date: "12/13/2025, 9:00:00 AM",
        icon: <CheckCircleIcon />,
    },
    {
        title: "Product Picked Up",
        date: "12/14/2025, 11:45:00 AM",
        icon: <LocalShippingIcon />,
    },
    {
        title: "Product Received by Vendor",
        date: "12/15/2025, 4:00:00 PM",
        icon: <CheckCircleIcon />,
    },
    {
        title: "Vendor Approved Final Refund",
        date: "12/15/2025, 5:00:00 PM",
        icon: <CheckCircleIcon />,
    },
    {
        title: "Refund Processed by Admin",
        date: "12/16/2025, 11:00:00 AM",
        icon: <CheckCircleIcon />,
    },
];


const ReturnOrderView: React.FC = () => {
    const isDelivered = true; // toggle based on order status
    const navigate = useNavigate();

    // Static order data


    // Map order status to workflow steps
    const workflowSteps = [
        "Request Raised",
        "Vendor Approved",
        "Admin Approved (Pickup)",
        "Picked Up",
        "Received by Vendor",
        "Final Admin Approval",
        "Refunded",
    ];

    const [currentStatus, setCurrentStatus] = useState("Awaiting Final Refund");

    const getActiveStep = (status: string) => {
        switch (status) {
            case "Awaiting Vendor Approval": return 0;
            case "Awaiting Admin Pickup": return 2;
            case "Awaiting Final Refund": return 5;
            case "Refunded": return 7; // Past the last step
            default: return 0;
        }
    };

    const activeStep = getActiveStep(currentStatus);
    const order = {
        id: "ORD12345",

        vendor: {
            name: "TechHub Electronics",
            email: "support@techhub.com",
            phone: "+971 55 123 4567",
            image: "/static/images/user_placeholder.png",
        },

        customerName: "John Doe",
        customerPhone: "123-456-7890",
        orderDateTime: "2025-11-19 14:30",
        amount: "250.00",
        method: "Credit Card",
        status: "Shipped",
        category: "Electronics",
        subCategory: "Mobile Phones",
        quantity: 2,
        expectedDelivery: "2025-11-25",
        notes: "Handle with care",
        paymentStatus: "Paid",
        courier: "FedEx",
        trackingNumber: "FX123456789",
        pickupDateTime: "2025-11-19 16:00",

        items: [
            {
                name: "iPhone 14",
                amount: "1200.00",
                quantity: 1,
                sku: "IPH14",
                commission: 10,
                netAmount: "1080.00",
                image: "/static/images/placeholder.png",
            },
            {
                name: "Samsung Galaxy S21",
                amount: "1000.00",
                quantity: 1,
                sku: "SGS21",
                commission: 8,
                netAmount: "920.00",
                image: "/static/images/placeholder.png",
            },
        ],

        activityLog: [
            { time: "2025-11-19 14:35", action: "Order placed", actor: "Customer" },
            { time: "2025-11-19 15:00", action: "Order shipped", actor: "Admin" },
        ],
    };

    const [openModal, setOpenModal] = useState(false);

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleRefundProceed = (amount: string, reason: string) => {
        setOpenModal(false);
        // For demo: set status to refunded if amount is provided
        if (amount) {
            setCurrentStatus("Refunded");
        }
    };

    // Mock returned items (just using the first item for demo)
    const returnedItems = [order.items[0]];

    return (
        <>
            <div className="view_order_page">
                <div className="main_title">
                    <h1>View Return & Refund</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                        <button onClick={() => navigate("/return-refund")}>
                            Return & Refund
                        </button>{" "}
                        - View
                    </p>
                </div>

                {/* Order Summary */}
                <div className="cards view_page">
                    <h3>Order Summary</h3>
                    <div className="gap_p">
                        <div className="w_33">
                            <h6>Order ID</h6>
                            <p>{order.id}</p>
                        </div>
                        <div className="w_33">
                            <h6>Order Date & Time</h6>
                            <p>{order.orderDateTime}</p>
                        </div>
                        <div className="w_33">
                            <h6>Payment Method</h6>
                            <p>{order.method}</p>
                        </div>

                        <div className="w_33">
                            <h6>Total Amount</h6>
                            <p>{order.amount}</p>
                        </div>
                        <div className="w_33">
                            <h6>Admin Commission</h6>
                            <p>20</p>
                        </div>
                        <div className="w_33">
                            <h6>Vendor Net Payout </h6>
                            <p>100</p>
                        </div>

                    </div>
                </div>

                {/* Total Item Details */}
                <div className="cards">
                    <CardContent>
                        <h3>Total Order Items</h3>
                        <Grid container spacing={2}>
                            {order.items?.map((item, idx) => (
                                <Grid item xs={12} md={6} key={idx}>
                                    <Card variant="outlined" sx={{ display: "flex", padding: 1 }}>
                                        <Box sx={{ width: 100, height: 100, mr: 2 }}>
                                            <img
                                                src={item.image || "/static/images/placeholder.png"}
                                                alt={item.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1">{item.name}</Typography>
                                            <Typography variant="body2">Amount: ${item.amount} | Qty: {item.quantity}</Typography>
                                            <Typography variant="body2">SKU: {item.sku}</Typography>
                                            <Typography variant="body2">Commission: {item.commission}%</Typography>
                                            <Typography variant="body2">Vendor Net: ${item.netAmount}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </div>

                {/* Returned Items Details */}
                <div className="cards">
                    <CardContent>
                        <h3>Returned Items</h3>
                        <Grid container spacing={2}>
                            {returnedItems.map((item, idx) => (
                                <Grid item xs={12} md={6} key={idx}>
                                    <Card variant="outlined" sx={{ display: "flex", padding: 1 }}>
                                        <Box sx={{ width: 100, height: 100, mr: 2 }}>
                                            <img
                                                src={item.image || "/static/images/placeholder.png"}
                                                alt={item.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1">{item.name}</Typography>
                                            <Typography variant="body2">Amount: ${item.amount} | Qty: {item.quantity}</Typography>
                                            <Typography variant="body2">SKU: {item.sku}</Typography>
                                            <Typography variant="body2">Commission: {item.commission}%</Typography>
                                            <Typography variant="body2">Vendor Net: ${item.netAmount}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </div>

                <div className="cards view_page">
                    <h3>Reason for return </h3>
                    <div className="gap_p">
                        <div className="w_33">
                            <h6>Reason</h6>
                            <p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Nemo, sapiente facilis, quos distinctio voluptas cupiditate voluptate cumque eos error quo reiciendis. Voluptate perspiciatis adipisci accusantium, quaerat optio culpa aut consequatur aspernatur. Ducimus distinctio consectetur eaque officia magnam facilis inventore amet velit eos obcaecati, assumenda, dignissimos consequuntur, a ullam omnis voluptas.</p>
                        </div>
                        <div className="w_33">
                            <h6>Return Date & Time</h6>
                            <p>{order.orderDateTime}</p>
                        </div>
                        <div className="w_33 vew_img no_radius">
                            <h6>Media</h6>
                            <figure>
                                <img src="/static/images/placeholder.png" alt="icon" />
                            </figure>
                        </div>
                        <div className="w_33">
                            <h6>Vendor Approval Status</h6>
                            <p>
                                {activeStep >= 5 ? "Product Received & Approved for Refund" :
                                    activeStep >= 1 ? "Initial Return Request Approved" : "Pending Vendor Action"}
                            </p>
                        </div>
                        <div className="w_100">
                            {activeStep === 2 && (
                                <div className="gap_p">
                                    <div className="control_group w_17">
                                        <Button className="btn btn_grey" onClick={() => setCurrentStatus("Awaiting Vendor Approval")}>Reject Pickup</Button>
                                    </div>
                                    <div className="control_group w_25">
                                        <Button className="btn btn_primary" onClick={() => setCurrentStatus("Awaiting Final Refund")}>
                                            Approve Pickup
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {activeStep === 5 && (
                                <div className="gap_p">
                                    <div className="control_group w_17">
                                        <Button className="btn btn_grey">Reject Return</Button>
                                    </div>
                                    <div className="control_group w_25">
                                        <Button className="btn btn_primary" onClick={() => { setOpenModal(true); setCurrentStatus("Refunded"); }}>
                                            Approve Final Refund
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {activeStep > 5 && (
                                <Typography variant="h6" color="success.main">Refund Successfully Processed</Typography>
                            )}
                            {activeStep < 2 && (
                                <Typography variant="body1" color="textSecondary">Waiting for Vendor Approval...</Typography>
                            )}
                        </div>

                    </div>
                </div>

                {/* Refund Details from Subadmin */}
                {/* <div className="cards view_page">
                    <h3>Refund Details (Processed by Vendor)</h3>
                    <div className="gap_p">
                        <div className="w_50">
                            <h6>Refunded Amount</h6>
                            <p>AED 1000</p>
                        </div>
                        <div className="w_50">
                            <h6>Reason for Refund Adjustment</h6>
                            <p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Aperiam repellat vitae odio! Reiciendis blanditiis quia facere odio? Laboriosam quisquam quas nesciunt velit ipsa repellat ut cum nam, voluptatem harum ducimus similique modi laudantium officia! Quaerat quia hic amet ratione voluptatibus, dolores libero unde vero corporis aliquid, ab, voluptates voluptate aut.</p>
                        </div>
                    </div>
                </div> */}

                <div className="cards view_page order_timeline">
                    <h3>Order Workflow</h3>
                    <Box sx={{ width: '100%', mb: 4, mt: 2 }}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {workflowSteps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <div className="flex12">
                        <div className="timeline">
                            {initialTimeline.map((item, index) => (
                                <div
                                    key={index}
                                    className={`timeline_item ${index < activeStep ? "completed" : ""}`}
                                >
                                    {/* Icon */}
                                    <div className="icon">
                                        {item.icon}
                                    </div>

                                    {/* Content */}
                                    <div className="content">
                                        <h4>{item.title}</h4>
                                        <span className="date">{item.date}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {isDelivered && (
                            <div className="delivery_image">
                                <h4>Order Delivered</h4>
                                <img
                                    src="/static/images/placeholder.png"
                                    alt="Order Delivered"
                                />
                            </div>
                        )}
                    </div>
                    {/* Delivered Image */}
                </div>

                {/* Delivery & Logistics */}
                <div className="cards view_page">
                    <h3>Delivery & Logistics</h3>
                    <div className="gap_p">
                        <div className="w_33">
                            <h6>Courier Name</h6>
                            <p>{order.courier || "-"}</p>
                        </div>
                        <div className="w_33">
                            <h6>Tracking Number</h6>
                            <p>{order.trackingNumber || "-"}</p>
                        </div>
                        <div className="w_33">
                            <h6>Pickup Date & Time</h6>
                            <p>{order.pickupDateTime || "-"}</p>
                        </div>
                    </div>
                </div>
                <div className="order_timeline">
                    <div className="flex12">

                        <div className="cards view_page">
                            <h3>Customer Details</h3>
                            <div className="gap_p view_page">
                                <div className="w_100 vew_img">
                                    <h6>User Image</h6>
                                    <figure>
                                        <img src="/static/images/user_placeholder.png" alt="" />
                                    </figure>
                                </div>
                                <div className="w_33">
                                    <h6>User Name</h6>
                                    <p>Jeo</p>
                                </div>
                                <div className="w_33">
                                    <h6>User Email</h6>
                                    <p>Jeogmail.com</p>
                                </div>
                                <div className="w_33">
                                    <h6>User Phone</h6>
                                    <p>892738623767</p>
                                </div>
                            </div>
                        </div>
                        {/* Vendor Details */}
                        <div className="cards view_page">
                            <h3>Vendor Details</h3>
                            <div className="gap_p view_page">
                                <div className="w_100 vew_img">
                                    <h6>Vendor Image</h6>
                                    <figure>
                                        <img
                                            src="/static/images/user_placeholder.png"
                                            alt="Vendor"
                                        />
                                    </figure>
                                </div>

                                <div className="w_33">
                                    <h6>Vendor Name</h6>
                                    <p>{order.vendor.name}</p>
                                </div>

                                <div className="w_33">
                                    <h6>Vendor Email</h6>
                                    <p>{order.vendor.email}</p>
                                </div>

                                <div className="w_33">
                                    <h6>Vendor Phone</h6>
                                    <p>{order.vendor.phone}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>






                {/* Activity Log */}
                <div className="cards view_page mt_20">
                    <h3>Activity Log</h3>
                    {order.activityLog?.length ? (
                        <ul>
                            {order.activityLog.map((log, idx) => (
                                <li key={idx}>
                                    <strong>{log.time}</strong>: {log.action} ({log.actor})
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No activity recorded.</p>
                    )}
                </div>

            </div>
            <TakeReturnAction
                open={openModal}
                onClose={handleCloseModal}
                setOpen={setOpenModal}
                onProceed={handleRefundProceed}
            />
        </>
    );
};

export default ReturnOrderView;
